<template>
  <!-- Modern overlay with backdrop blur -->
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Modern header with glass morphism -->
      <div class="modal-header">
        <div class="header-content">
          <!-- Mobile toggle button for header details -->
          <button
            @click="toggleHeaderDetails"
            class="header-details-toggle"
            :class="{ 'expanded': showHeaderDetails }"
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"/>
            </svg>
            <span>{{ showHeaderDetails ? 'Hide details' : 'Show details' }}</span>
          </button>

          <div class="header-details" :class="{ 'collapsed': !showHeaderDetails }">
            <div class="header-detail-item">
              <div class="detail-icon">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{ $t('photo.details.uploadedBy') }}</span>
                <span class="detail-value">{{ currentPhoto.username || 'Unknown' }}</span>
              </div>
            </div>

            <div class="header-detail-item">
              <div class="detail-icon">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{ $t('photo.details.dateTaken') }}</span>
                <span class="detail-value">{{ currentPhoto.date_taken ? formatDate(currentPhoto.date_taken) : $t('photo.details.unknown') }}</span>
              </div>
            </div>

            <div class="header-detail-item">
              <div class="detail-icon">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{ $t('photo.details.location') }}</span>
                <span class="detail-value">
                  {{ currentPhoto.latitude && currentPhoto.longitude ?
                     `${currentPhoto.latitude.toFixed(6)}, ${currentPhoto.longitude.toFixed(6)}` :
                     $t('photo.details.noGPS') }}
                </span>
              </div>
            </div>

          </div>
        </div>
        <button @click="closeModal" class="close-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Alert messages with modern styling -->
      <div v-if="error" class="alert alert-error">
        <div class="alert-icon">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
        </div>
        <span>{{ error }}</span>
      </div>

      <div v-if="success" class="alert alert-success">
        <div class="alert-icon">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </div>
        <span>{{ success }}</span>
      </div>

      <div class="photo-detail">
        <!-- Modern image container with carousel -->
        <div class="photo-section">
          <div class="photo-image-container">
            <!-- Carousel navigation -->
            <div v-if="markerPhotos.length > 1" class="carousel-nav">
              <button 
                @click="previousPhoto" 
                class="nav-btn nav-prev"
                :disabled="currentPhotoIndex === 0"
              >
                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                </svg>
              </button>
              <button 
                @click="nextPhoto" 
                class="nav-btn nav-next"
                :disabled="currentPhotoIndex === markerPhotos.length - 1"
              >
                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </button>
            </div>

            <img 
              :src="currentImageUrl" 
              :alt="currentPhoto.original_name" 
              class="photo-image"
              @load="imageLoaded = true"
              @error="imageError = true"
            />
            <div v-if="!imageLoaded && !imageError" class="image-loading">
              <div class="loading-spinner"></div>
              <p>Loading image...</p>
            </div>
            <div v-if="imageError" class="image-error">
              <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
              <p>Failed to load image</p>
            </div>
            
            <!-- Image overlay with download button -->
            <div v-if="imageLoaded && isLoggedIn" class="image-overlay">
              <button
                @click="downloadPhoto"
                class="download-btn"
                :disabled="downloading"
                :title="$t('photo.actions.download')"
              >
                <svg v-if="!downloading" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                </svg>
                <div v-else class="loading-spinner small"></div>
              </button>
            </div>

            <!-- Carousel thumbnails -->
            <div v-if="markerPhotos.length > 1" class="carousel-thumbnails">
              <div class="thumbnails-container">
                <button
                  v-for="(photo, index) in markerPhotos"
                  :key="photo.id"
                  @click="setCurrentPhoto(index)"
                  class="thumbnail-btn"
                  :class="{ 'active': index === currentPhotoIndex }"
                >
                  <img 
                    :src="photosAPI.getImage(photo.filename)" 
                    :alt="photo.original_name"
                    class="thumbnail-image"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Modern info panel -->
        <div class="info-panel">
          <!-- Upload to Location Card -->
          <div v-if="isLoggedIn" class="info-card upload-card">
            <div class="card-header">
              <h3>
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
                </svg>
                Add Photos to Location
              </h3>
            </div>
            
            <div class="card-content">
              <p class="upload-description">Upload additional photos to this location. GPS coordinates and location will be automatically set.</p>
              <button 
                @click="requestUploadToMarker" 
                class="btn btn-primary upload-btn"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
                </svg>
                <span>Add Photos</span>
              </button>
            </div>
          </div>

          <!-- Tags Card -->
          <div class="info-card">
            <div class="card-header">
              <h3>
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 2 2 2h11c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"/>
                </svg>
                {{ $t('photo.details.tags') }}
              </h3>
              <button 
                v-if="!editingTags && currentPhoto.tags && currentPhoto.tags.length > 0 && isPhotoOwner"
                @click="startEditingTags" 
                class="edit-btn"
                :title="$t('photo.tags.edit')"
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              </button>
            </div>
            
            <div class="card-content">
              <!-- Display mode -->
              <div v-if="!editingTags">
                <div v-if="currentPhoto.tags && currentPhoto.tags.length > 0" class="tags-container">
                  <span 
                    v-for="tag in currentPhoto.tags" 
                    :key="tag" 
                    class="tag"
                    @click="searchTag(tag)"
                  >
                    {{ tag }}
                  </span>
                </div>
                <div v-else class="empty-state">
                  <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24" opacity="0.3">
                    <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 2 2 2h11c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"/>
                  </svg>
                  <p>{{ $t('photo.details.noTags') }}</p>
                  <button 
                    v-if="isPhotoOwner"
                    @click="startEditingTags" 
                    class="btn btn-primary btn-sm"
                  >
                    {{ $t('photo.tags.add') }}
                  </button>
                </div>
              </div>

              <!-- Edit mode -->
              <div v-else class="tags-edit-container">
                <div class="input-group">
                  <input
                    v-model="editTagsValue"
                    ref="tagsInput"
                    type="text"
                    :placeholder="$t('upload.form.tagsPlaceholder')"
                    class="modern-input"
                    :class="{ 'error': tagEditError }"
                    @keyup.enter="saveTagChanges"
                    @keyup.escape="cancelTagEdit"
                  />
                  <div v-if="tagEditError" class="error-message">
                    {{ tagEditError }}
                  </div>
                  <p class="input-hint">{{ $t('upload.requirements.tags') }}</p>
                </div>
                
                <div class="action-group">
                  <button 
                    @click="saveTagChanges" 
                    class="btn btn-primary"
                    :disabled="savingTags || !editTagsValue.trim()"
                  >
                    <div v-if="savingTags" class="loading-spinner small"></div>
                    <span v-else>{{ $t('photo.tags.save') }}</span>
                  </button>
                  <button 
                    @click="cancelTagEdit" 
                    class="btn btn-secondary"
                    :disabled="savingTags"
                  >
                    {{ $t('photo.tags.cancel') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions Card -->
          <div v-if="isLoggedIn" class="info-card actions-card">
            <div class="card-header">
              <h3>
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                {{ $t('photo.details.actions') }}
              </h3>
            </div>
            
            <div class="card-content">
              <div v-if="!showDeleteConfirm" class="action-buttons">
                <button
                  v-if="isLoggedIn"
                  @click="downloadPhoto"
                  class="btn btn-primary action-btn"
                  :disabled="downloading"
                >
                  <svg v-if="!downloading" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                  <div v-else class="loading-spinner small"></div>
                  <span>{{ $t('photo.actions.download') }}</span>
                </button>
                
                <button 
                  v-if="isPhotoOwner"
                  @click="showDeleteConfirm = true" 
                  class="btn btn-danger action-btn"
                  :disabled="deleting"
                >
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                  <span>{{ $t('photo.actions.delete') }}</span>
                </button>
              </div>
              
              <div v-else class="delete-confirm">
                <div class="confirm-content">
                  <div class="confirm-icon">
                    <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                    </svg>
                  </div>
                  <p class="confirm-text">{{ $t('photo.delete.confirm') }}</p>
                </div>
                <div class="confirm-buttons">
                  <button 
                    @click="confirmDelete" 
                    class="btn btn-danger"
                    :disabled="deleting"
                  >
                    <div v-if="deleting" class="loading-spinner small"></div>
                    <span v-else>{{ $t('photo.delete.yes') }}</span>
                  </button>
                  <button 
                    @click="showDeleteConfirm = false" 
                    class="btn btn-secondary"
                    :disabled="deleting"
                  >
                    {{ $t('photo.delete.cancel') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Modal -->
    <!-- Removed MarkerUploadModal from here - it will be handled by parent component -->
  </div>
</template>

<script>
import { photosAPI } from '../services/api'

export default {
  name: 'PhotoDetailModal',
  components: {
    // Removed MarkerUploadModal
  },
  props: {
    photo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      imageLoaded: false,
      imageError: false,
      showDeleteConfirm: false,
      deleting: false,
      error: '',
      success: '',
      downloading: false,
      editingTags: false,
      editTagsValue: '',
      tagEditError: '',
      savingTags: false,
      showHeaderDetails: false,
      markerPhotos: [],
      currentPhotoIndex: 0,
      photosAPI
    }
  },
  computed: {
    imageUrl() {
      return photosAPI.getImage(this.photo.filename)
    },
    currentPhoto() {
      return this.markerPhotos[this.currentPhotoIndex] || this.photo
    },
    currentImageUrl() {
      return photosAPI.getImage(this.currentPhoto.filename)
    },
    isLoggedIn() {
      return !!localStorage.getItem('token')
    },
    isPhotoOwner() {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
      return this.currentPhoto.user_id === currentUser.id
    }
  },
  async mounted() {
    await this.loadMarkerPhotos()
  },
  methods: {
    async loadMarkerPhotos() {
      if (!this.photo.marker_group_id) {
        // If no marker group ID, just use the single photo
        this.markerPhotos = [this.photo]
        this.currentPhotoIndex = 0
        return
      }

      try {
        const response = await photosAPI.getByMarkerGroup(this.photo.marker_group_id)
        this.markerPhotos = response.data
        
        // Find the index of the current photo
        const currentIndex = this.markerPhotos.findIndex(p => p.id === this.photo.id)
        this.currentPhotoIndex = currentIndex >= 0 ? currentIndex : 0
      } catch (error) {
        console.error('Error loading marker photos:', error)
        // Fallback to single photo
        this.markerPhotos = [this.photo]
        this.currentPhotoIndex = 0
      }
    },

    closeModal() {
      this.$emit('close')
    },

    formatDate(dateString) {
      if (!dateString) return this.$t('photo.details.unknown');
      
      let date;
      // Handle EXIF date format (YYYY:MM:DD HH:MM:SS)
      if (typeof dateString === 'string' && dateString.includes(':')) {
        // Convert EXIF format to ISO format
        const isoString = dateString.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
        date = new Date(isoString);
      } else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return this.$t('photo.details.unknown');
      }
      
      // Use current locale for date formatting
      const locale = this.$i18n.locale === 'de' ? 'de-DE' : 'en-US';
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    searchTag(tag) {
      // Emit event to parent to search for this tag
      this.$emit('search-tag', tag)
      this.closeModal()
    },

    toggleHeaderDetails() {
      this.showHeaderDetails = !this.showHeaderDetails
    },

    setCurrentPhoto(index) {
      this.currentPhotoIndex = index
      this.imageLoaded = false
      this.imageError = false
    },

    previousPhoto() {
      if (this.currentPhotoIndex > 0) {
        this.setCurrentPhoto(this.currentPhotoIndex - 1)
      }
    },

    nextPhoto() {
      if (this.currentPhotoIndex < this.markerPhotos.length - 1) {
        this.setCurrentPhoto(this.currentPhotoIndex + 1)
      }
    },

    async handleUploadSuccess(uploadedPhotos) {
      this.success = `${uploadedPhotos.length} photo(s) added to location successfully!`
      
      // Reload marker photos to include new uploads
      await this.loadMarkerPhotos()
      
      // Emit event to parent to refresh the main photos list
      this.$emit('photos-updated')
      
      // Clear success message after a delay
      setTimeout(() => {
        this.success = ''
      }, 3000)
    },

    async confirmDelete() {
      this.deleting = true
      this.error = ''
      this.success = ''

      try {
        await photosAPI.delete(this.currentPhoto.id)
        this.success = this.$t('photo.delete.success')
        
        // Remove the deleted photo from markerPhotos
        this.markerPhotos = this.markerPhotos.filter(p => p.id !== this.currentPhoto.id)
        
        // Adjust current index if necessary
        if (this.markerPhotos.length === 0) {
          // No more photos in this marker group, close modal
          setTimeout(() => {
            this.$emit('photo-deleted', this.currentPhoto.id)
            this.closeModal()
          }, 1000)
        } else {
          // Adjust current index
          if (this.currentPhotoIndex >= this.markerPhotos.length) {
            this.currentPhotoIndex = this.markerPhotos.length - 1
          }
          
          // Emit delete event to parent
          setTimeout(() => {
            this.$emit('photo-deleted', this.currentPhoto.id)
          }, 1000)
        }
        
      } catch (error) {
        this.error = error.response?.data?.message || this.$t('photo.delete.error')
        this.showDeleteConfirm = false
      } finally {
        this.deleting = false
      }
    },

    async downloadPhoto() {
      this.downloading = true
      this.error = ''
      this.success = ''

      try {
        const response = await photosAPI.download(this.currentPhoto.id)
        
        // Create blob from response
        const blob = new Blob([response.data], { type: 'image/jpeg' })
        
        // Create download link
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = this.currentPhoto.original_name
        
        // Trigger download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up
        URL.revokeObjectURL(link.href)
        
        this.success = this.$t('photo.download.success')
        setTimeout(() => {
          this.success = ''
        }, 3000)
        
      } catch (error) {
        this.error = error.response?.data?.message || this.$t('photo.download.error')
      } finally {
        this.downloading = false
      }
    },

    startEditingTags() {
      this.editingTags = true
      this.editTagsValue = this.currentPhoto.tags ? this.currentPhoto.tags.join(', ') : ''
      this.tagEditError = ''
      
      // Focus input after DOM update
      this.$nextTick(() => {
        if (this.$refs.tagsInput) {
          this.$refs.tagsInput.focus()
        }
      })
    },

    cancelTagEdit() {
      this.editingTags = false
      this.editTagsValue = ''
      this.tagEditError = ''
    },

    async saveTagChanges() {
      if (!this.editTagsValue.trim()) {
        this.tagEditError = this.$t('upload.validation.tagsRequired')
        return
      }

      // Parse tags
      const tags = this.editTagsValue
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)

      if (tags.length === 0) {
        this.tagEditError = this.$t('upload.validation.tagsRequired')
        return
      }

      this.savingTags = true
      this.tagEditError = ''
      this.error = ''
      this.success = ''

      try {
        await photosAPI.updateTags(this.currentPhoto.id, tags)
        
        // Update the current photo's tags
        this.currentPhoto.tags = tags
        
        // Update the photo in markerPhotos array
        const photoIndex = this.markerPhotos.findIndex(p => p.id === this.currentPhoto.id)
        if (photoIndex >= 0) {
          this.markerPhotos[photoIndex].tags = tags
        }
        
        this.success = this.$t('photo.tags.updateSuccess')
        this.editingTags = false
        this.editTagsValue = ''
        
        // Emit event to parent
        this.$emit('tags-updated')
        
        setTimeout(() => {
          this.success = ''
        }, 3000)
        
      } catch (error) {
        this.tagEditError = error.response?.data?.message || this.$t('photo.tags.updateError')
      } finally {
        this.savingTags = false
      }
    },

    async requestUploadToMarker() {
      let markerGroupId = this.currentPhoto.marker_group_id
      
      // If the photo doesn't have a marker_group_id (older photos), generate one
      if (!markerGroupId) {
        markerGroupId = `marker_${this.currentPhoto.user_id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        
        try {
          // Update the photo in the database with the new marker_group_id
          const response = await photosAPI.updateMarkerGroup(this.currentPhoto.id, markerGroupId)
          
          // Update the local photo object
          this.currentPhoto.marker_group_id = markerGroupId
          
          // Update the photo in markerPhotos array
          const photoIndex = this.markerPhotos.findIndex(p => p.id === this.currentPhoto.id)
          if (photoIndex >= 0) {
            this.markerPhotos[photoIndex].marker_group_id = markerGroupId
          }
          
        } catch (error) {
          console.error('Error updating photo with marker group ID:', error)
          this.error = 'Failed to prepare photo for additional uploads. Please try again.'
          return
        }
      }
      
      this.$emit('request-upload-to-marker', {
        markerGroupId: markerGroupId,
        tags: this.currentPhoto.tags || []
      })
    }
  }
}
</script>

<style scoped>
/* Modern modal overlay with backdrop blur */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
  animation: fadeIn 0.3s ease-out;
}

.modal-content {
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  border-radius: 24px;
  /* Maintain the same beautiful shadow and glow effects */
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  -webkit-backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  position: relative;
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translate3d(0, 40px, 0) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
}

/* Modern header with glass morphism */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem 1.5rem 0.75rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  position: relative;
}

.header-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.photo-title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
  word-break: break-word;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.header-details {
  display: flex;
  gap: 2rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.header-detail-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.6);
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.detail-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 8px;
  color: #6366f1;
  flex-shrink: 0;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  word-break: break-word;
}

.close-btn {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
  transform: scale(1.05);
}

/* Modern alert messages */
.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  margin: 0 2rem 1rem;
  border-radius: 12px;
  font-weight: 500;
  animation: slideDown 0.3s ease-out;
}

.alert-error {
  background: rgba(249, 115, 22, 0.1);
  border: 1px solid rgba(249, 115, 22, 0.2);
  color: #ea580c;
}

.alert-success {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  color: #047857;
}

.alert-icon {
  flex-shrink: 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Modern photo detail layout */
.photo-detail {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  padding: 1.5rem 2rem 2rem;
  overflow-y: auto;
  max-height: calc(90vh - 140px);
}

/* Modern photo section */
.photo-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.photo-image-container {
  position: relative;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  overflow: hidden;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.photo-image {
  width: 100%;
  height: auto;
  max-height: 70vh;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.image-loading,
.image-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.image-error svg {
  opacity: 0.5;
}

.image-overlay {
  position: absolute;
  top: 1rem;
  right: 1rem;
  opacity: 0;
  transition: all 0.3s ease;
}

.photo-image-container:hover .image-overlay {
  opacity: 1;
}

.download-btn {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6366f1;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.download-btn:hover:not(:disabled) {
  background: #6366f1;
  color: white;
  transform: scale(1.05);
}

/* Modern info panel */
.info-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
}

/* Modern info cards */
.info-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.info-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.card-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-header svg {
  color: #6366f1;
}

.edit-btn {
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  color: #6366f1;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-btn:hover {
  background: rgba(99, 102, 241, 0.2);
  transform: scale(1.05);
}

.card-content {
  padding: 1.5rem;
}

/* Modern tags */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  color: #6366f1;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  position: relative;
  overflow: hidden;
}

.tag::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.5s;
}

.tag:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.tag:hover::before {
  left: 100%;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.empty-state p {
  margin: 0;
  font-style: italic;
}

/* Modern forms */
.tags-edit-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.modern-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.8);
  font-family: inherit;
}

.modern-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  background: white;
}

.modern-input.error {
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
}

.error-message {
  color: #f97316;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
}

.action-group {
  display: flex;
  gap: 0.75rem;
}

/* Modern buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  position: relative;
  overflow: hidden;
  font-family: inherit;
  min-height: 44px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: 1px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
}

.btn-secondary {
  background: rgba(107, 114, 128, 0.1);
  color: #4b5563;
  border: 1px solid rgba(107, 114, 128, 0.3);
  box-shadow: 0 2px 4px rgba(107, 114, 128, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.15);
  color: #374151;
  border-color: rgba(107, 114, 128, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.2);
}

.btn-danger {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
  border: 1px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(249, 115, 22, 0.3);
}

.btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(249, 115, 22, 0.4);
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  min-height: 36px;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  flex-direction: column;
}

.action-btn {
  flex: 1;
  min-width: 0;
}

/* Delete confirmation */
.delete-confirm {
  background: rgba(249, 115, 22, 0.05);
  border: 1px solid rgba(249, 115, 22, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
}

.confirm-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.confirm-icon {
  flex-shrink: 0;
  color: #f97316;
}

.confirm-text {
  margin: 0;
  color: #1f2937;
  font-weight: 500;
  flex: 1;
}

.confirm-buttons {
  display: flex;
  gap: 0.75rem;
}

.confirm-buttons .btn {
  flex: 1;
}

/* Loading spinner */
.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner.small {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive design */
@media (max-width: 1024px) {
  /* Modal spacing for tablets and mobile - same as mobile view */
  .modal-overlay {
    padding: 1rem;
  }

  .modal-content {
    max-height: 95vh;
    border-radius: 16px;
    /* Maintain the same beautiful shadow and glow effects */
    box-shadow: 
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
    -webkit-backdrop-filter: blur(20px);
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  .modal-header {
    padding: 1.25rem 1.5rem 0.75rem;
  }

  .photo-title {
    font-size: 1.4rem;
  }

  /* Layout changes for tablets and mobile */
  .photo-detail {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0 1.5rem 1.5rem;
  }

  /* Mobile/Tablet: Photo first, info panel second */
  .photo-section {
    order: 1;
  }

  .info-panel {
    order: 2;
  }

  /* Show toggle button on tablets and mobile */
  .header-details-toggle {
    display: flex !important;
  }

  /* Apply mobile-friendly header details to tablets and mobile */
  .header-details {
    gap: 0.5rem;
    flex-direction: column;
    align-items: stretch;
    margin-top: 0.75rem;
  }

  /* Collapsed state - hide header details */
  .header-details.collapsed {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
    padding-top: 0;
    padding-bottom: 0;
    transform: translateY(-10px);
  }

  .header-detail-item {
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(8px);
    flex-direction: row;
    align-items: center;
    border-radius: 8px;
    margin: 0;
    width: 100%;
    min-height: 40px;
  }

  .detail-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    background: rgba(99, 102, 241, 0.15);
    border-radius: 6px;
  }

  .detail-content {
    flex: 1;
    min-width: 0;
  }

  .detail-label {
    font-size: 0.65rem;
    margin-bottom: 0.125rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .detail-value {
    font-size: 0.75rem;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.2;
  }

  .card-header,
  .card-content {
    padding: 1rem;
  }

  .action-buttons {
    gap: 0.75rem;
  }

  .confirm-buttons {
    flex-direction: column;
  }
}

/* Small mobile specific adjustments */
@media (max-width: 480px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-header {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    position: relative;
    padding: 1rem;
  }

  .header-content {
    flex: 1;
  }

  .close-btn {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
  }

  .header-details-toggle {
    margin-top: 0;
  }

  /* Extra mobile-friendly header details */
  .header-details {
    gap: 0.5rem;
    width: 100%;
  }

  .header-detail-item {
    padding: 0.625rem 0.875rem;
    gap: 0.625rem;
    border-radius: 8px;
  }

  .detail-icon {
    width: 28px;
    height: 28px;
  }

  .detail-label {
    font-size: 0.65rem;
    font-weight: 700;
  }

  .detail-value {
    font-size: 0.75rem;
    line-height: 1.3;
  }

  /* Photo section adjustments for small mobile */
  .photo-image-container {
    min-height: 250px;
  }

  .photo-detail {
    padding: 0 1rem 1rem;
  }

  .tags-container {
    gap: 0.375rem;
  }

  .tag {
    padding: 0.375rem 0.75rem;
    font-size: 0.8rem;
  }

  .action-group {
    flex-direction: column;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .modal-content {
    background: rgba(17, 24, 39, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .modal-header {
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.9) 0%, rgba(31, 41, 55, 0.8) 100%);
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }

  .photo-title {
    color: #f9fafb;
  }

  .upload-date {
    color: #9ca3af;
  }

  .close-btn {
    background: rgba(17, 24, 39, 0.8);
    border-color: rgba(255, 255, 255, 0.1);
    color: #9ca3af;
  }

  .info-card {
    background: rgba(17, 24, 39, 0.7);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .card-header {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }

  .card-header h3 {
    color: #f3f4f6;
  }

  .detail-label {
    color: #9ca3af;
  }

  .detail-value {
    color: #f3f4f6;
  }

  .modern-input {
    background: rgba(17, 24, 39, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
    color: #f9fafb;
  }

  .modern-input:focus {
    background: rgba(17, 24, 39, 0.95);
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
    color: #f9fafb;
  }

  .modern-input.error {
    background: rgba(17, 24, 39, 0.8);
    border-color: #f97316;
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.2);
  }

  .photo-image-container {
    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .empty-state {
    color: #9ca3af;
  }

  .input-hint {
    color: #9ca3af;
  }

  .alert-error {
    background: rgba(249, 115, 22, 0.2);
    border: 1px solid rgba(249, 115, 22, 0.4);
    color: #fed7aa;
  }

  .alert-success {
    background: rgba(16, 185, 129, 0.2);
    border: 1px solid rgba(16, 185, 129, 0.4);
    color: #86efac;
  }

  .header-detail-item {
    background: rgba(17, 24, 39, 0.6);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .detail-icon {
    background: rgba(99, 102, 241, 0.2);
    color: #818cf8;
  }

  .detail-label {
    color: #9ca3af;
  }

  .detail-value {
    color: #f3f4f6;
  }

  .info-card {
    background: rgba(17, 24, 39, 0.7);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .confirm-text {
    color: #f9fafb;
  }

  .btn-secondary {
    background: rgba(75, 85, 99, 0.2);
    color: #d1d5db;
    border: 1px solid rgba(75, 85, 99, 0.4);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .btn-secondary:hover:not(:disabled) {
    background: rgba(75, 85, 99, 0.3);
    color: #f3f4f6;
    border-color: rgba(75, 85, 99, 0.6);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
}

/* Dark mode mobile adjustments */
@media (prefers-color-scheme: dark) and (max-width: 1024px) {
  .header-detail-item {
    background: rgba(17, 24, 39, 0.8);
    border-color: rgba(255, 255, 255, 0.15);
    padding: 0.5rem 0.75rem;
    min-height: 40px;
  }

  .detail-icon {
    background: rgba(99, 102, 241, 0.3);
    color: #818cf8;
    width: 24px;
    height: 24px;
  }

  .detail-label {
    color: #9ca3af;
    font-size: 0.65rem;
    font-weight: 700;
  }

  .detail-value {
    color: #f3f4f6;
    font-size: 0.75rem;
    line-height: 1.2;
  }

  .header-details-toggle {
    background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
    border-color: rgba(129, 140, 248, 0.3);
    color: #1f2937;
    box-shadow: 0 2px 8px rgba(129, 140, 248, 0.3);
    padding: 0.5rem 0.75rem;
    min-height: 36px;
    font-size: 0.8rem;
  }

  .header-details-toggle:hover {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    box-shadow: 0 4px 12px rgba(129, 140, 248, 0.4);
  }
}

/* Mobile header details toggle */
.header-details-toggle {
  display: none; /* Hidden on desktop */
  align-items: center;
  gap: 0.4rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.75rem;
  min-height: 36px;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.header-details-toggle:hover {
  background: linear-gradient(135deg, #5855eb 0%, #7c3aed 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.header-details-toggle svg {
  transition: transform 0.2s ease;
}

.header-details-toggle.expanded svg {
  transform: rotate(180deg);
}

/* Header details container with animation */
.header-details {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

/* Photo counter */

/* Carousel navigation */
.carousel-nav {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 1rem;
  z-index: 10;
  pointer-events: none;
}

.nav-btn {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6366f1;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
}

.nav-btn:hover:not(:disabled) {
  background: #6366f1;
  color: white;
  transform: scale(1.1);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4);
}

.nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

/* Carousel thumbnails */
.carousel-thumbnails {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

/* Hide carousel thumbnails on mobile to prevent blocking the image */
@media (max-width: 768px) {
  .carousel-thumbnails {
    display: none;
  }
}

.thumbnails-container {
  display: flex;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 0.75rem;
  max-width: 90vw;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.thumbnails-container::-webkit-scrollbar {
  display: none;
}

.thumbnail-btn {
  background: none;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.thumbnail-btn.active {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
}

.thumbnail-btn:hover {
  transform: scale(1.05);
}

.thumbnail-image {
  width: 60px;
  height: 45px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
}

/* Upload card */
.upload-card {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.upload-card .card-header {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%);
  border-bottom-color: rgba(34, 197, 94, 0.2);
}

.upload-card .card-header h3 {
  color: #059669;
}

.upload-card .card-header svg {
  color: #10b981;
}

.upload-description {
  margin: 0 0 1rem 0;
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.5;
}

.upload-btn {
  width: 100%;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: 1px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.3);
}

.upload-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
}
</style> 